// src/components/HomeScreen.js
import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { locationData } from '../locationData'; // Our nested location data
import './HomeScreen.css';

function HomeScreen() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  // For logging geolocation
  const [location, setLocation] = useState(null);

  // For multi-level location filtering
  const [selectedBorder, setSelectedBorder] = useState('National'); // "National", "Mexico Border", or "Canada Border"
  const [selectedState, setSelectedState] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  // Fetched featured videos
  const [featuredVideos, setFeaturedVideos] = useState([]);

  // 1. Log user visit and get geolocation
  useEffect(() => {
    async function logVisit() {
      if (user) {
        try {
          await addDoc(collection(db, 'logs'), {
            userId: user.uid,
            email: user.email,
            timestamp: new Date(),
            location: location || 'unknown'
          });
        } catch (error) {
          console.error('Error logging visit:', error);
        }
      }
    }
    logVisit();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => console.error('Geolocation error:', error)
      );
    }
  }, [user, location]);

  // 2. Fetch featured videos from Firestore based on final location selection
  useEffect(() => {
    async function fetchFeaturedVideos() {
      try {
        // Construct a "locationKey" to match your Firestore doc.
        // For example, if you store final city name in doc.location = "Phoenix"
        // or if you store something like "Arizona-Maricopa County-Phoenix"
        let locationKey = 'National';
        if (selectedBorder !== 'National') {
          locationKey = selectedCity || selectedCounty || selectedState || selectedBorder;
        }

        // Example: we store doc.location = locationKey, doc.approved = true
        const q = query(
          collection(db, 'posts'),
          where('approved', '==', true),
          where('location', '==', locationKey)
        );

        const querySnapshot = await getDocs(q);
        const videos = [];
        querySnapshot.forEach((doc) => {
          videos.push({ id: doc.id, ...doc.data() });
        });
        setFeaturedVideos(videos);
      } catch (error) {
        console.error('Error fetching featured videos:', error);
      }
    }
    fetchFeaturedVideos();
  }, [selectedBorder, selectedState, selectedCounty, selectedCity]);

  // 3. Handle changes in each dropdown
  const handleBorderChange = (e) => {
    const value = e.target.value;
    setSelectedBorder(value);
    setSelectedState('');
    setSelectedCounty('');
    setSelectedCity('');
  };

  const handleStateChange = (e) => {
    const value = e.target.value;
    setSelectedState(value);
    setSelectedCounty('');
    setSelectedCity('');
  };

  const handleCountyChange = (e) => {
    const value = e.target.value;
    setSelectedCounty(value);
    setSelectedCity('');
  };

  const handleCityChange = (e) => {
    const value = e.target.value;
    setSelectedCity(value);
  };

  // 4. Logout
  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  // Helper to get next-level data from locationData
  const getStates = () => {
    if (selectedBorder === 'National') return [];
    return Object.keys(locationData[selectedBorder].states || {});
  };

  const getCounties = () => {
    if (!selectedState || selectedBorder === 'National') return [];
    return Object.keys(locationData[selectedBorder].states[selectedState].counties || {});
  };

  const getCities = () => {
    if (!selectedCounty || !selectedState || selectedBorder === 'National') return [];
    return locationData[selectedBorder].states[selectedState].counties[selectedCounty] || [];
  };

  return (
    <div className="home-container">
      {/* Header */}
     

      {/* Welcome & Location Info */}
      <div className="welcome-section">
        <h3>Welcome, {user ? user.email : 'Guest'}!</h3>
        {location ? (
          <p>Your location: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}</p>
        ) : (
          <p>Location not available</p>
        )}
      </div>

      {/* Multi-Level Filters */}
      <div className="filter-section">
        <h4>Choose Your Location</h4>
        
        {/* Border Dropdown */}
        <div className="filter-row">
        <label>Border:</label>
        <select value={selectedBorder} onChange={handleBorderChange}>
          <option value="National">National</option>
          <option value="Mexico Border">Mexico Border</option>
          <option value="Canada Border">Canada Border</option>
        </select>
        </div>
        {/* State Dropdown (only if not National) */}

        {selectedBorder !== 'National' && (
          <div className="filter-row">
          <>
            <label>State:</label>
            <select value={selectedState} onChange={handleStateChange}>
              <option value="">-- Select State --</option>
              {getStates().map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
            </>
            </div>
        )}

        {/* County Dropdown (only if a State is selected) */}
        {selectedState && (
          <>
          <div className="filter-row">
            <label>County:</label>
            <select value={selectedCounty} onChange={handleCountyChange}>
              <option value="">-- Select County --</option>
              {getCounties().map((county) => (
                <option key={county} value={county}>
                  {county}
                </option>
              ))}
            </select>
            </div>
          </>
          
        )}

        {/* City Dropdown (only if a County is selected) */}
        {selectedCounty && (
          <>
          <div className="filter-row">
            <label>City:</label>
            <select value={selectedCity} onChange={handleCityChange}>
              <option value="">-- Select City --</option>
              {getCities().map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
            </div>
          </>
        )}
      </div>

      {/* Featured Videos Section */}
      <div className="featured-videos">
        <h3>
          Featured Videos (
          {selectedCity || selectedCounty || selectedState || selectedBorder}
          )
        </h3>
        {featuredVideos.length === 0 ? (
          <p>No videos found for this location.</p>
        ) : (
          <div className="videos-list">
            {featuredVideos.map((video) => (
              <div key={video.id} className="video-card">
                <img
                  src={video.thumbnailUrl || '/video-placeholder.jpg'}
                  alt="Video Thumbnail"
                />
                <div className="video-info">
                  <h4>{video.title || 'Untitled'}</h4>
                  <p>{video.description || 'No description available'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit News Button */}
      <button className="submit-btn" onClick={() => navigate('/submit')}>
        Submit News
      </button>
    </div>
  );
}

export default HomeScreen;
